/**
 * MenuSyncDialog — Configuration et déclenchement de la synchronisation des menus
 * depuis HACCP / Plat Témoin.
 *
 * Réservé aux gestionnaires (manager/admin). La clé API n'est jamais réaffichée :
 * on indique seulement si une clé est configurée. L'agent peut tester la connexion
 * et lancer un import manuel sur une période.
 */

import { useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { Dialog } from '@/components/ui/Dialog';
import { Button, Field, Input, Alert } from '@/components/ui';
import type { MenuSyncConfig } from '@roronoa-collectivites/shared-types';

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function MenuSyncDialog({ open, onClose, onImported }: { open: boolean; onClose: () => void; onImported?: () => void }) {
  const [cfg, setCfg] = useState<MenuSyncConfig | null>(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [siteId, setSiteId] = useState('');
  const [repas, setRepas] = useState<'petit_dej' | 'midi' | 'soir'>('midi');
  const [syncAuto, setSyncAuto] = useState(false);
  const [from, setFrom] = useState(ymd(new Date()));
  const [to, setTo] = useState(ymd(new Date(Date.now() + 30 * 86400_000)));

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null); setInfo(null); setApiKey('');
    (async () => {
      try {
        const c = await api.scolaire.menus.sync.getConfig();
        setCfg(c);
        setBaseUrl(c.haccpBaseUrl ?? '');
        setSiteId(c.siteId ?? '');
        setRepas(c.repas);
        setSyncAuto(c.syncAuto);
      } catch (e) { setErr(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    })();
  }, [open]);

  async function enregistrer() {
    setBusy(true); setErr(null); setInfo(null);
    try {
      const c = await api.scolaire.menus.sync.setConfig({
        haccpBaseUrl: baseUrl.trim() || null,
        siteId: siteId.trim() || null,
        repas,
        syncAuto,
        // apiKey envoyée seulement si saisie (sinon on garde l'existante)
        ...(apiKey !== '' ? { apiKey } : {}),
      });
      setCfg(c); setApiKey('');
      setInfo('Configuration enregistrée.');
    } catch (e) { setErr(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }

  async function tester() {
    setBusy(true); setErr(null); setInfo(null);
    try {
      const r = await api.scolaire.menus.sync.test();
      setInfo(`Connexion réussie : ${r.menusTrouves} menu(s) trouvé(s) sur les 7 prochains jours.`);
    } catch (e) { setErr(e instanceof ApiException ? e.message : 'Échec du test de connexion'); }
    finally { setBusy(false); }
  }

  async function importer() {
    setBusy(true); setErr(null); setInfo(null);
    try {
      const r = await api.scolaire.menus.sync.import(from, to);
      const ignore = r.ignoresManuels > 0 ? ` (${r.ignoresManuels} menu(s) saisi(s) à la main préservé(s))` : '';
      setInfo(`${r.importes} menu(s) importé(s)${ignore}.`);
      onImported?.();
    } catch (e) { setErr(e instanceof ApiException ? e.message : "Échec de l'import"); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Synchronisation des menus (HACCP)"
      description="Connectez le compte HACCP / Plat Témoin de la commune pour importer automatiquement les menus de cantine."
      size="lg"
      footer={<Button variant="ghost" onClick={onClose} disabled={busy}>Fermer</Button>}>
      <div className="space-y-4">
        {err && <Alert variant="error">{err}</Alert>}
        {info && <Alert variant="success">{info}</Alert>}

        <Field label="URL de l'instance HACCP" htmlFor="ms-url">
          <Input id="ms-url" inputMode="url" placeholder="https://haccp.exemple.fr" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        </Field>

        <Field label={cfg?.cleConfiguree ? 'Clé API (laisser vide pour conserver la clé actuelle)' : 'Clé API HACCP (scope lecture)'} htmlFor="ms-key">
          <Input id="ms-key" type="password" autoComplete="off"
            placeholder={cfg?.cleConfiguree ? '•••••••• clé configurée' : 'collez la clé API'}
            value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
        </Field>
        {cfg?.cleConfiguree && (
          <p className="-mt-2 text-2xs text-ink-subtle">
            Une clé est déjà enregistrée (chiffrée). Saisissez-en une nouvelle pour la remplacer.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Site HACCP (optionnel)" htmlFor="ms-site">
            <Input id="ms-site" placeholder="laisser vide = tous" value={siteId} onChange={(e) => setSiteId(e.target.value)} />
          </Field>
          <Field label="Repas" htmlFor="ms-repas">
            <select id="ms-repas" value={repas} onChange={(e) => setRepas(e.target.value as typeof repas)}
              className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-3 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine">
              <option value="petit_dej">Petit-déjeuner</option>
              <option value="midi">Midi (déjeuner)</option>
              <option value="soir">Soir</option>
            </select>
          </Field>
        </div>

        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={syncAuto} onChange={(e) => setSyncAuto(e.target.checked)}
            className="h-4 w-4 rounded border-border text-cantine focus-visible:ring-2 focus-visible:ring-cantine" />
          Synchronisation automatique quotidienne
        </label>

        <div className="flex flex-wrap gap-2">
          <Button variant="cantine" loading={busy} onClick={() => void enregistrer()}>Enregistrer</Button>
          <Button variant="secondary" disabled={busy} onClick={() => void tester()}>Tester la connexion</Button>
        </div>

        {cfg?.derniereSyncAt && !Number.isNaN(new Date(cfg.derniereSyncAt).getTime()) && (
          <p className="text-2xs text-ink-subtle">
            Dernière synchro : {new Date(cfg.derniereSyncAt).toLocaleString('fr-FR')}
            {cfg.dernierStatut ? ` · statut : ${cfg.dernierStatut}` : ''}
          </p>
        )}

        <div className="border-t border-border pt-4">
          <span className="mb-2 block text-sm font-medium text-ink">Importer maintenant</span>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Du" htmlFor="ms-from"><Input id="ms-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
            <Field label="Au" htmlFor="ms-to"><Input id="ms-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
          </div>
          <Button className="mt-2" variant="cantine" loading={busy} onClick={() => void importer()}>Importer les menus</Button>
          <p className="mt-2 text-2xs text-ink-subtle">
            Les menus déjà saisis à la main ne sont jamais écrasés par l'import.
          </p>
        </div>
      </div>
    </Dialog>
  );
}
